import React, { useEffect, useState } from 'react';
import { Event, LocationType, Shift, Signup } from '@amzn/red-velvet-api';
import { ColumnLayout, Link, Spinner, TextContent } from '@amzn/awsui-components-react';
import Grid from '@amzn/awsui-components-react/polaris/grid';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import Container from '@amzn/awsui-components-react/polaris/container';
import Header from '@amzn/awsui-components-react/polaris/header';
import SegmentedControl from '@amzn/awsui-components-react/polaris/segmented-control';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Badge from '@amzn/awsui-components-react/polaris/badge';
import { ManageLinks } from '../manageLinks';
import { useUser } from '../../hooks/user';
import { CustomIcon } from '../customIcon';
import { filterBetterShifts } from '../../utils/filterUtils';
import { useCarouselDisplayCount } from '../../hooks/shifts/shiftDisplay';
import { AlertInfo, useSingleBetterEventFromSearchParam } from '../../hooks/betterEvents';
import { SHIFT_WIDTH_PIXELS, ShiftCarousel } from './shiftCarousel';
import { EventVolunteerShiftTable } from './eventVolunteerShiftTable';
import { formatPhoneForDisplay } from '../../utils/phoneUtils';
import {
  DISPLAY_ATTENDANCE_INFO,
  DISPLAY_ATTRIBUTES_TYPE,
  DISPLAY_DATE_INFO,
  DISPLAY_SHIFT_LOCATION_INFO,
  DISPLAY_SIGNUPS_REMAINING_INFO_WITH_CONDITION,
} from '../shifts/shiftInfo';
import * as sanitizer from '@amzn/aws-console-html-sanitizer';
import { EventDetailsTable } from '../attendance/coordinator/coordinatorEventOwner';
import { getOptionFromValue, getValueFromSearchParam } from '../../utils/url';
import { LoginData } from '../../hooks/login/login';
import { canManageGivehubEvent, canManageVCEvent } from '../../utils/shiftUtils';
import { featureIsEnabled, useFeatures } from '../../hooks/features';
import { ADMIN_EVENT_PAGE_ACCESS, EVENT_CREATION, SHIFT_WAITLIST } from '../../utils/constants';
import { SelectProps } from '@amzn/awsui-components-react/polaris/select';
import { Flashbar } from '@amzn/awsui-components-react';
import { publishKatalMetric } from '../katalAnalytics';
import { shouldBlockAsVCDraft } from '../../utils/eventUtils';

export const dateDisplayOptions: Intl.DateTimeFormatOptions = {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
};

export const timeDisplayOptions: Intl.DateTimeFormatOptions = {
  timeZoneName: 'shortGeneric',
  hour: 'numeric',
  minute: 'numeric',
};

function Contact({ name, email, phone }: { name: string; email?: string; phone?: string }) {
  const { t } = useTranslation();
  const alias = email?.includes('@amazon.') && email.substring(0, email.indexOf('@'));

  return (
    <TextContent>
      <p>{name}</p>
      {email?.includes('@amazon.') ? (
        <p>
          <Link
            href={`https://phonetool.amazon.com/users/${alias}`}
            external
            variant="primary"
            data-aci-analytics-name="details_contactPhonetoolAlias"
          >
            {alias}
          </Link>
        </p>
      ) : null}
      {email ? (
        <p>
          {t('eventDetails.contact.email')}:{' '}
          <a data-aci-analytics-name="details_contactEmail" href={`mailto:${email}`}>
            {email}
          </a>
        </p>
      ) : null}
      {(() => {
        const displayPhone = phone ? formatPhoneForDisplay(phone) : '';
        return displayPhone ? (
          <p>
            {t('eventDetails.contact.phone')}:{' '}
            <a data-aci-analytics-name="details_contactPhone" href={`tel:${phone}`}>
              {displayPhone}
            </a>
          </p>
        ) : null;
      })()}
    </TextContent>
  );
}

interface GoBackLocationState {
  goBackPath?: string;
}

export function GoBackLink() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const goBack = () => {
    const goBackPath = (location.state as GoBackLocationState | null)?.goBackPath;
    if (goBackPath) navigate(goBackPath);
    // If the user navigated here from search, we want to return to the search they were previously using so we maintain
    // any filters they were using
    else if (window.history.length > 1) navigate(-1);
    // However, if the user simply pasted a link into the browser, we don't have any filters to load and should just go
    // to the normal search
    else navigate('/');
  };

  return (
    <p className="go-back">
      <Link data-aci-analytics-name="details_backToSearch" onFollow={goBack}>
        {t('fullEvent.goBack')}
      </Link>
    </p>
  );
}

export function FullEventDisplay() {
  const [{ event, loading }] = useSingleBetterEventFromSearchParam();
  const user = useUser();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const features = useFeatures();

  if (loading || !user) {
    return (
      <Container
        header={
          <Header>
            <GoBackLink />
            <h1>{t('eventDetails.loadingEvent')}</h1>
          </Header>
        }
      >
        <Spinner />
      </Container>
    );
  }

  if (!event?.event) {
    return (
      <Container
        header={
          <Header>
            <GoBackLink />
            <h1>{event?.alert?.message}</h1>
          </Header>
        }
      ></Container>
    );
  }

  const eventData = {
    event: event.event,
    shifts: event.shifts,
    signups: event.signups,
    alert: event.alert,
    user,
  };

  const signupRequired =
    getValueFromSearchParam(searchParams, 'coordinator-action', '') === 'signup';
  const isAdmin = featureIsEnabled(ADMIN_EVENT_PAGE_ACCESS, features, searchParams);
  const isDeleted = !!event.event?.vcDeleted;

  const ownsEvent =
    isAdmin || canManageGivehubEvent(event.event, user) || canManageVCEvent(event.event, user);

  return (
    <div className="full-event">
      <EventHeader {...eventData} ownsEvent={!!ownsEvent} />
      {!isDeleted && (
        <>
          {ownsEvent && !signupRequired ? (
            <EventOwnerShiftView {...eventData} />
          ) : (
            <EventVolunteerShiftView {...eventData} />
          )}
          <EventDetailsSection {...eventData} />
        </>
      )}
    </div>
  );
}

interface FullEventContentProps {
  event: Event;
  shifts: Shift[];
  signups: Signup[];
  alert?: AlertInfo;
  user: LoginData;
  ownsEvent?: boolean;
}

export function EventHeader({ event, ownsEvent }: FullEventContentProps) {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const features = useFeatures();

  const selectedTimezone =
    searchParams.get('timezone') || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const isEventCreationEnabled = featureIsEnabled(EVENT_CREATION, features, searchParams);
  const isBlocked = shouldBlockAsVCDraft(event);
  useEffect(() => {
    if (isEventCreationEnabled && ownsEvent && isBlocked) {
      publishKatalMetric('createEvent_draftAlertViewEvent', event.eventId);
    }
  }, [isEventCreationEnabled, ownsEvent, isBlocked, event.eventId]);

  let displayedLocation: string;
  let locationColor: 'grey' | 'blue' | 'green';

  const locType = event.location?.locationType;
  if (locType === LocationType.VIRTUAL) {
    displayedLocation = t('events:eventDetailsStep.virtual');
    locationColor = 'grey';
  } else if (isEventCreationEnabled && event.vcCreated) {
    // VC-created events: 3-label set driven by locationType
    displayedLocation =
      locType === LocationType.ON_SITE
        ? t('events:eventDetailsStep.onCampus')
        : t('events:eventDetailsStep.offCampus');
    locationColor = locType === LocationType.ON_SITE ? 'green' : 'blue';
  } else if (event.location?.amazonBuilding) {
    // Manually corrected to Amazon building (any flag state)
    displayedLocation = t('events:eventDetailsStep.onCampus');
    locationColor = 'green';
  } else if (locType) {
    // GiveHub event without amazonBuilding
    displayedLocation = t('events:eventDetailsStep.inPerson');
    locationColor = 'blue';
  } else {
    // No locationType set — fall back to city presence
    const hasCity = event.location?.city;
    displayedLocation = hasCity
      ? t('events:eventDetailsStep.inPerson')
      : t('events:eventDetailsStep.virtual');
    locationColor = hasCity ? 'blue' : 'grey';
  }

  const startDay = new Date(event.startTimestamp || '').toLocaleDateString(i18n.language, {
    ...dateDisplayOptions,
    timeZone: selectedTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  const endDay = event.endTimestamp
    ? new Date(event.endTimestamp).toLocaleDateString(i18n.language, {
        ...dateDisplayOptions,
        timeZone: selectedTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
    : null;

  return (
    <Container>
      <GoBackLink />
      <Header variant="h1">{event.title}</Header>
      <p className="full-subheader">
        {event.startTimestamp ? `${startDay}${endDay ? ` - ${endDay}` : ''} • ` : null}
        {event.location && event.location.city
          ? t('eventDisplay.benevityButBetterPhysicalLocation', {
              city: event.location.city,
              state: event.location.province,
              country: event.location.country,
            })
          : null}
        <span className="location-badge">
          <Badge color={locationColor}>{displayedLocation}</Badge>
        </span>
      </p>
      <p>{event.shortDescription}</p>
      {event.vcDeleted && (
        <Flashbar
          items={[
            {
              type: 'error',
              header: t('fullEvent.deletedEventHeader'),
              content: t('fullEvent.deletedEventContent'),
            },
          ]}
        />
      )}
      {isEventCreationEnabled && ownsEvent && isBlocked && !event.vcDeleted && (
        <Flashbar
          items={[
            {
              type: 'warning',
              header: t('fullEvent.draftEventWarningHeader'),
              // Same warning message for both draft and archived alerts, per Figma
              content: t('fullEvent.draftEventWarning'),
            },
          ]}
        />
      )}
    </Container>
  );
}

export function EventVolunteerShiftView({ event, shifts, signups }: FullEventContentProps) {
  const { t } = useTranslation();
  const shiftsPerPage = useCarouselDisplayCount(SHIFT_WIDTH_PIXELS);
  const [searchParams] = useSearchParams();
  const features = useFeatures();
  const isEventCreationEnabled = featureIsEnabled(EVENT_CREATION, features, searchParams);
  const isShiftWaitlistEnabled = featureIsEnabled(SHIFT_WAITLIST, features, searchParams);
  // CCE-7232: block the volunteer shift table on draft / unpublished VC events (they surface on the
  // coordinator Drafts tab) so it can't be used to sign up or track attendance — mirroring the
  // coordinator surfaces (coordinatorAttendance / coordinatorManageSignupsPageForShift). EventHeader
  // already renders the draft-warning Flashbar for owners, so render nothing here when blocked.
  const isBlocked = shouldBlockAsVCDraft(event);

  if (isShiftWaitlistEnabled && isBlocked) {
    return null;
  }

  const filteredShifts = filterBetterShifts(shifts, signups, searchParams);
  const shiftsHeader =
    filteredShifts && filteredShifts.length > 0 ? 'eventDetails.shifts' : 'eventDetails.noShifts';

  const displayAttributes: DISPLAY_ATTRIBUTES_TYPE[] = [
    DISPLAY_DATE_INFO,
    ...(isEventCreationEnabled ? ([DISPLAY_SHIFT_LOCATION_INFO] as const) : []),
    DISPLAY_SIGNUPS_REMAINING_INFO_WITH_CONDITION,
    DISPLAY_ATTENDANCE_INFO,
  ];

  return (
    <div className="shiftSection">
      {isShiftWaitlistEnabled ? (
        <EventVolunteerShiftTable event={event} />
      ) : (
        <ShiftCarousel
          carouselTitle={t(shiftsHeader)}
          event={event}
          paginationType="bottom"
          cardsPerRow={[{ cards: shiftsPerPage }]}
          displayAttributes={displayAttributes}
        />
      )}
    </div>
  );
}

export function EventOwnerShiftTable({ event }: FullEventContentProps) {
  const [searchParams] = useSearchParams();
  const features = useFeatures();
  const isShiftWaitlistEnabled = featureIsEnabled(SHIFT_WAITLIST, features, searchParams);
  // Single boundary (matching the volunteer table) once ShiftWaitlist is live; keep the original
  // Container-wrapped table when the flag is off.
  return isShiftWaitlistEnabled ? (
    <EventDetailsTable eventId={event.eventId} />
  ) : (
    <Container>
      <EventDetailsTable eventId={event.eventId} />
    </Container>
  );
}

type OwnerShiftViewId = 'coordinator' | 'volunteer';

// Owner-only view switcher on the Event Details page (cce-7136): coordinators/admins flip between the
// coordinator management table (default) and the volunteer-facing shift table so they can preview what
// volunteers see and sign themselves up. Gated behind ShiftWaitlist; with the flag off, owners keep the
// coordinator table and no toggle renders.
export function EventOwnerShiftView(props: FullEventContentProps) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const features = useFeatures();
  const isShiftWaitlistEnabled = featureIsEnabled(SHIFT_WAITLIST, features, searchParams);
  // Both waitlist email deep links (?action=joinWaitlist / ?action=leaveWaitlist) fire inside the
  // volunteer table, but owners/admins default to the coordinator view — which would silently swallow
  // the link (e.g. an owner testing a volunteer email). Deep-link intent wins the DEFAULT only; the
  // toggle still works normally afterwards. Both waitlist CTAs land on the event page (cce-4614).
  const deepLinkAction = searchParams.get('action')?.toLowerCase();
  const isWaitlistDeepLink =
    deepLinkAction === 'joinwaitlist' || deepLinkAction === 'leavewaitlist';
  const [selectedView, setSelectedView] = useState<OwnerShiftViewId>(
    isWaitlistDeepLink ? 'volunteer' : 'coordinator'
  );

  // The volunteer shift table is blocked on draft / unpublished VC events (CCE-7232) so it can't be
  // used to sign up or track attendance — the same guard EventVolunteerShiftView applies for volunteers.
  // On those events (and when the flag is off) owners keep the coordinator table with no toggle.
  if (!isShiftWaitlistEnabled || shouldBlockAsVCDraft(props.event)) {
    return <EventOwnerShiftTable {...props} />;
  }

  return (
    <SpaceBetween size="s">
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <SegmentedControl
          selectedId={selectedView}
          onChange={({ detail }) => setSelectedView(detail.selectedId as OwnerShiftViewId)}
          label={t('fullEvent.viewToggle.label')}
          options={[
            { id: 'coordinator', text: t('fullEvent.viewToggle.coordinator') },
            { id: 'volunteer', text: t('fullEvent.viewToggle.volunteer') },
          ]}
        />
      </div>
      {selectedView === 'coordinator' ? (
        <EventOwnerShiftTable {...props} />
      ) : (
        <EventVolunteerShiftTable event={props.event} />
      )}
    </SpaceBetween>
  );
}

export function EventDetailsSection({ event, user }: FullEventContentProps) {
  const { t } = useTranslation(['translation', 'events']);
  const [searchParams] = useSearchParams();
  const features = useFeatures();
  const isEventCreationEnabled = featureIsEnabled(EVENT_CREATION, features, searchParams);
  const signupRequired =
    getValueFromSearchParam(searchParams, 'coordinator-action', '') === 'signup';
  const isManageLinksEnabled = canManageGivehubEvent(event, user) && signupRequired;

  const focusOptions = t('events:focus', { returnObjects: true }) as SelectProps.Options;
  const activityOptions = t('events:activity', { returnObjects: true }) as SelectProps.Options;

  const contactItems = event.contacts?.map((contact, index) => (
    <Contact
      key={index}
      name={contact.name || ''}
      email={contact.email || ''}
      phone={contact.phone || ''}
    />
  ));

  return (
    <Container>
      <Grid
        gridDefinition={[{ colspan: { default: 12, xs: 9 } }, { colspan: { default: 12, xs: 3 } }]}
      >
        <SpaceBetween direction="vertical" size="s">
          <Header variant="h2">{t('eventDetails.about')}</Header>
          <div
            className="event-description-content"
            dangerouslySetInnerHTML={{
              __html: sanitizer.sanitizeHtmlString(event.description || '', {
                keepFormattingTags: true,
              }),
            }}
          />
        </SpaceBetween>
        <ColumnLayout borders="horizontal" columns={1}>
          <Header variant="h2">{t('fullEvent.details')}</Header>
          <div className="sidebarSection">
            <h3>
              <CustomIcon name="contact" /> {t('fullEvent.primaryContact')}
            </h3>
            <div className="sidebarContent">
              {event && event.contacts && event.contacts.length > 0 ? (
                <SpaceBetween direction="vertical" size="m">
                  {contactItems}
                </SpaceBetween>
              ) : (
                <Contact
                  name={'ACI Global Volunteering'}
                  email={'volunteering@amazon.com'}
                  phone={''}
                />
              )}
            </div>
          </div>
          <div className="sidebarSection">
            <h3>
              <CustomIcon name="location" /> {t('eventDetails.location')}
            </h3>
            <div className="sidebarContent">
              {event.location?.locationType === LocationType.VIRTUAL ? (
                <div>
                  <p>{t('virtualLocation')}</p>
                  {event.location.notes?.trim() && (
                    <div className="sidebar-option-margin"> {event.location.notes}</div>
                  )}
                </div>
              ) : (
                <div>
                  <div>{event.location.name}</div>
                  {!event.location.city &&
                    !event.location.street &&
                    event.location.address_string && <div>{event.location.address_string}</div>}
                  {event.location.street ? <div>{event.location.street}</div> : null}
                  {event.location.city ? (
                    <div>
                      {event.location.city}, {event.location.province} {event.location.postal_code}
                    </div>
                  ) : null}
                  {event.location.notes ? (
                    <div className="sidebar-option-margin">{event.location.notes}</div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
          {event.skills && event.skills.length > 0 && (
            <div className="sidebarSection">
              <h3>
                <CustomIcon name="skills" /> {t('eventDetails.skills')}
              </h3>
              <div className="sidebarContent">
                <ul>
                  {event.skills.map((skill) => (
                    <li key={skill}>{skill}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          {event.waiver?.acceptance === 'required' && (
            <div className="sidebarSection">
              <h3>
                <CustomIcon name="requirements" /> {t('eventDetails.requirements')}
              </h3>
              <div className="sidebarContent">
                <ul>
                  <li key="waiver">{t('eventDetails.waiverRequired')}</li>
                </ul>
              </div>
            </div>
          )}
          {isEventCreationEnabled && event.vcCreated && event.focusArea && (
            <div className="sidebarSection">
              <h3>
                <CustomIcon name="focusArea" /> {t('events:eventDetailsStep.focusArea')}
              </h3>
              <div className="sidebarContent">
                {getOptionFromValue(event.focusArea, focusOptions)?.label || event.focusArea}
              </div>
            </div>
          )}
          {isEventCreationEnabled && event.vcCreated && event.primaryActivity && (
            <div className="sidebarSection">
              <h3>
                <CustomIcon name="primaryActivity" /> {t('events:eventDetailsStep.primaryActivity')}
              </h3>
              <div className="sidebarContent">
                {getOptionFromValue(event.primaryActivity, activityOptions)?.label ||
                  event.primaryActivity}
              </div>
            </div>
          )}
          {isManageLinksEnabled ? <ManageLinks id={event.eventId} /> : null}
        </ColumnLayout>
      </Grid>
    </Container>
  );
}
